import { useState } from "react";
import { Check } from "lucide-react";
import GuaranteedRentClosedModal from "./GuaranteedRentClosedModal";

export default function GuaranteedRentComparison({ city }) {
  const [showModal, setShowModal] = useState(false);

  return (
    <section style={{ backgroundColor: "#F4F1E7", padding: "clamp(60px,7vw,96px) clamp(20px,5vw,80px)" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "#5C7268", marginBottom: 12, marginTop: 0 }}>
            Guaranteed Rent vs Airbnb Management
          </p>
          <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "clamp(28px, 4vw, 44px)", letterSpacing: "-0.02em", lineHeight: 1.15, color: "#112215", margin: "0 auto 14px", maxWidth: 640 }}>
            Choose your income style.
          </h2>
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 16, color: "#5C7268", lineHeight: 1.6, maxWidth: 560, margin: "0 auto" }}>
            With guaranteed rent we take a long-term lease and operate your property as serviced accommodation — paying you the same fixed amount every month. With Airbnb management you keep ownership of the income stream and earn more, but it varies month to month.
          </p>
        </div>

        <div style={{ background: "#fff", borderRadius: 16, overflow: "hidden", border: "1px solid #E8E4D5", marginBottom: 48 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr", gap: 16, padding: "18px 24px", background: "#112215", color: "#fff" }} className="gr-table-header">
            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>Property Type</div>
            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", textAlign: "right" }}>Guaranteed Rent</div>
            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", textAlign: "right" }}>Airbnb Potential</div>
          </div>

          {city.properties.map(({ type, area, gr, airbnb, uplift }, i) => (
            <div
              key={type}
              style={{
                display: "grid",
                gridTemplateColumns: "1.5fr 1fr 1fr",
                gap: 16,
                padding: "20px 24px",
                borderTop: i === 0 ? "none" : "1px solid #E8E4D5",
                alignItems: "center",
              }}
              className="gr-table-row"
            >
              <div>
                <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: 15, color: "#112215", margin: 0 }}>{type}</p>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: "#5C7268", margin: "4px 0 0" }}>{area}</p>
              </div>
              <div style={{ textAlign: "right" }}>
                <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 16, color: "#112215", margin: 0 }}>{gr}</p>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: "#5C7268", margin: "2px 0 0" }}>per month</p>
              </div>
              <div style={{ textAlign: "right" }}>
                <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 16, color: "#CD533B", margin: 0 }}>{airbnb}</p>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: "#5C7268", margin: "2px 0 0" }}>{uplift}</p>
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 48 }} className="gr-feature-grid">
          <div style={{ background: "#fff", borderRadius: 14, padding: "32px 28px", border: "1px solid #E8E4D5" }}>
            <h3 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 20, color: "#112215", marginBottom: 20, marginTop: 0 }}>Guaranteed Rent</h3>
            <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 14 }}>
              {[
                "Fixed monthly income, paid on the same date every month",
                "Zero void periods — you're paid whether occupied or not",
                "We become your tenant on a multi-year contract",
                "Cleaning, maintenance & property care all included",
                "Optional light refurbishment (subject to approval)",
                "No guest management, no arrears, no deposit disputes",
              ].map((feature) => (
                <li key={feature} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                  <div style={{ width: 18, height: 18, borderRadius: "50%", background: "#F4F1E7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                    <Check size={12} color="#CD533B" strokeWidth={3} />
                  </div>
                  <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, color: "#5C7268", lineHeight: 1.5 }}>{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          <div style={{ background: "#fff", borderRadius: 14, padding: "32px 28px", border: "1px solid #E8E4D5" }}>
            <h3 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 20, color: "#112215", marginBottom: 20, marginTop: 0 }}>Airbnb Management</h3>
            <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 14 }}>
              {[
                "Earn 40–80% more than long-term rent",
                "Dynamic pricing optimised daily",
                "Full guest communication & check-ins",
                "Professional cleaning & linen",
                "Multi-platform listing (16+ sites)",
                "Live owner dashboard & reports",
              ].map((feature) => (
                <li key={feature} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                  <div style={{ width: 18, height: 18, borderRadius: "50%", background: "#F4F1E7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                    <Check size={12} color="#CD533B" strokeWidth={3} />
                  </div>
                  <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, color: "#5C7268", lineHeight: 1.5 }}>{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div style={{ textAlign: "center" }}>
          <button
            onClick={() => setShowModal(true)}
            style={{
              background: "#112215", color: "#fff", border: "none", borderRadius: 8,
              padding: "14px 32px", fontFamily: "'Inter', sans-serif", fontSize: 15, fontWeight: 600,
              cursor: "pointer", transition: "background 0.2s",
            }}
            onMouseEnter={e => e.currentTarget.style.background = "#1A3A2A"}
            onMouseLeave={e => e.currentTarget.style.background = "#112215"}
          >
            Get your personalised estimate
          </button>
        </div>
      </div>

      {showModal && <GuaranteedRentClosedModal onClose={() => setShowModal(false)} />}

      <style>{`
        @media (max-width: 768px) {
          .gr-table-header, .gr-table-row { grid-template-columns: 1fr !important; gap: 12px !important; }
          .gr-table-header > div:nth-child(2), .gr-table-header > div:nth-child(3) { display: none !important; }
          .gr-table-row > div:nth-child(2), .gr-table-row > div:nth-child(3) { text-align: left !important; border-top: 1px solid #E8E4D5 !important; padding-top: 8px !important; margin-top: 4px !important; }
          .gr-feature-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}